import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { switchMap, take, map } from 'rxjs/operators';

import { MindCatalogItem } from '../models/mind-catalog.model';
import { ProductV2 } from '../models/product-v2.model';
import { MindCatalogStore } from '../store/mind/mind-catalog.store';
import { MindProductMapper } from '../store/mind/mind-product.mapper';
import { LocationFacade } from '../store/location/location.facade';
import {
  findMindCatalogProductIndex,
  resolveMindCatalogGroup,
} from '../store/mind/mind-catalog-group.util';
import { applyMindCatalogImageAt } from '../utils/channel-content.util';

import { ChannelConfigService } from './channel-config.service';
import { MindCatalogService } from './mind-catalog.service';

export interface MindProductDetailLoaded {
  product: ProductV2;
  categoryAfterResolve: string;
}

/**
 * Carrega detalhe de produto MIND (catálogo) para a página de produto.
 */
@Injectable({ providedIn: 'root' })
export class ProductPageMindDetailService {
  private mindCatalogService = inject(MindCatalogService);
  private mindStore = inject(MindCatalogStore);
  private locationFacade = inject(LocationFacade);
  private channelConfigService = inject(ChannelConfigService);

  loadByCatalogProductId(
    productId: number,
    currentCategory: string,
    currentProductUuid: string
  ): Observable<MindProductDetailLoaded> {
    return this.locationFacade.location$.pipe(
      take(1),
      switchMap(loc => {
        const cep = (loc?.cep ?? '').replace(/\D/g, '');
        const number = (loc?.addressNumber ?? '').trim();
        if (!cep || !number) {
          return throwError(() => new Error('MIND_LOCATION'));
        }
        return this.mindCatalogService.getCatalogProductById(productId, cep, number);
      }),
      map((item: MindCatalogItem) => {
        const catalog = this.mindStore.snapshot;
        let categoryAfterResolve = currentCategory;
        const group = resolveMindCatalogGroup(
          catalog,
          currentCategory,
          currentProductUuid
        );
        if (group?.title) {
          categoryAfterResolve = group.title;
        }
        const groupId = group?.groupId ?? 'mind';
        const categoryTitle = group?.title ?? categoryAfterResolve;
        const mapped = MindProductMapper.mapItemToProduct(
          item,
          groupId,
          categoryTitle
        );
        const product = applyMindCatalogImageAt(
          mapped,
          this.channelConfigService.mindCatalogImageUrls,
          findMindCatalogProductIndex(group?.items, mapped.uuid)
        );
        return { product, categoryAfterResolve };
      })
    );
  }
}
